import { Fragment, h } from 'preact';

import TravelTypeSelector from '../TravelTypeSelector/TravelTypeSelector';
import PaxAmountSelector from '../PaxAmountSelector/PaxAmountSelector';
import { LocationSelector } from '../LocationSelector/LocationSelector';
import { LocationType, WidgetTab } from '../../common';
import { WidgetTestVariant } from '../../config';
import { actions, Actions, BookingState, connectWithStyles } from '../../store';
import { DateSelector, TravelDateType } from '../DateSelector/DateSelector';
import ContinueButton from '../ContinueButton/ContinueButton';
import StartingPrice from '../StartingPrice/StartingPrice';
import { PromoCodeButton } from '../PromoCode';
import { WithStyles } from '../../style';
import * as styles from './MultiCityLayout.scss';
import { FinText } from '../../i18n';
import AirCalendar from '../AirCalendar/AirCalendar';

export interface MultiCityLayoutProps {
  setFocusElement: (elem: HTMLElement) => void;
}

interface StoreProps {
  currentTab: WidgetTab;
  testVariant: WidgetTestVariant;
  enablePromoCode: boolean;
  directFlightsNotAvailable: boolean;
}

type EffectiveProps = MultiCityLayoutProps & StoreProps & WithStyles & Actions;

const mapStateToProps = ({ currentTab, testVariant, enablePromoCode, price }: BookingState): StoreProps => ({
  currentTab,
  testVariant,
  enablePromoCode,
  directFlightsNotAvailable: price.weeklyFlights?.length === 0,
});

export const MultiCityLayout = connectWithStyles<MultiCityLayoutProps>([styles], { mapStateToProps, actions })(
  ({ currentTab, testVariant, enablePromoCode, setFocusElement }: EffectiveProps) => {
    const isAward = currentTab === WidgetTab.AWARD;

    return (
      <Fragment>
        <AirCalendar />
        <TravelTypeSelector
          class="bw-travelType travelType white-bg border contentItem rounded"
          data-test-traveltype-selector
        />
        <PaxAmountSelector class="bw-pax-amount passengers contentItem white-bg border rounded" />
        <h2 class="flight-one-title font-body-1 ps-xsmall-t">
          <FinText id="bookingWidget.departure" />
        </h2>
        <div className={'locations white-bg border rounded'}>
          <LocationSelector
            locationType={LocationType.ORIGIN}
            isAward={isAward}
            testVariant={testVariant}
            activeLocationIndex={0}
          />
          <LocationSelector
            locationType={LocationType.DESTINATION}
            isAward={isAward}
            testVariant={testVariant}
            activeLocationIndex={0}
          />
        </div>
        <DateSelector
          class="bw-departure departure contentItem white-bg border rounded"
          type={TravelDateType.DEPARTURE}
          isAward={isAward}
        />
        <h2 class="flight-two-title font-body-1 ps-xsmall-t">
          <FinText id="bookingWidget.return" />
        </h2>
        <div className={'locations-two white-bg border rounded'}>
          <LocationSelector
            locationType={LocationType.ORIGIN}
            isAward={isAward}
            testVariant={testVariant}
            activeLocationIndex={1}
          />
          <LocationSelector
            locationType={LocationType.DESTINATION}
            isAward={isAward}
            testVariant={testVariant}
            activeLocationIndex={1}
          />
        </div>
        <DateSelector
          class="bw-return return contentItem white-bg border rounded"
          type={TravelDateType.RETURN}
          isAward={isAward}
        />

        <div class="bw-promo promo">
          <div class="wrap fill">
            {enablePromoCode && currentTab === WidgetTab.FLIGHT && (
              <PromoCodeButton class="bw-promoCode promoCode flex flex--middle promo-code-cta mr-small-t" />
            )}
          </div>
        </div>

        <div class="bw-cta callToAction">
          {!isAward && <StartingPrice class="totalPrice" data-test-starting-price />}
          <ContinueButton
            class="submit"
            ref={(elem: ContinueButton) => elem && setFocusElement(elem?.base as HTMLElement)}
          />
        </div>
      </Fragment>
    );
  }
);
